import { useState } from 'react';
import { message } from 'antd';
import fileService from '../../../services/fileService';

export const useFileUpload = (stepForm) => {
    const [fileList, setFileList] = useState({});
    const [previewUrls, setPreviewUrls] = useState({});
    const [uploading, setUploading] = useState({});
    const [uploadProgress, setUploadProgress] = useState({});

    // 处理文件上传 - 统一消息和朋友圈的文件上传处理
    const handleFileUpload = async ({ file, onSuccess, onError, onProgress, fieldKey, mediaType, useTempMedia = false, stepType = 'MESSAGE' }) => {
        // 判断是否为朋友圈附件上传 - 移到外部以便在catch块中也能访问
        const isMomentAttachment = stepType === 'MOMENTS' || fieldKey.startsWith('moment');
        
        try {
            // 更新上传状态 - 使用复合键来区分不同步骤类型的上传
            const uploadKey = `${stepType}_${fieldKey}`;
            setUploading(prev => ({ ...prev, [uploadKey]: true, [fieldKey]: true }));
            setUploadProgress(prev => ({ ...prev, [uploadKey]: 0, [fieldKey]: 0 }));

            // 显示上传进度
            const updateProgress = (percent) => {
                setUploadProgress(prev => ({ ...prev, [uploadKey]: percent, [fieldKey]: percent }));
                if (onProgress) {
                    onProgress({ percent });
                }
            };

            // 从localStorage获取租户信息
            const tenantStr = localStorage.getItem('tenant');
            if (!tenantStr) {
                message.error('未获取到租户信息，请重新登录');
                if (onError) {
                    onError(new Error('未获取到租户信息'));
                }
                return;
            }
            const tenant = JSON.parse(tenantStr);
            const tenantId = tenant.id;

            // 根据不同类型调用不同上传方法
            let result;

            if (isMomentAttachment) {
                // 朋友圈附件上传 - 使用专用API
                console.log('使用朋友圈专用API上传附件:', mediaType);
                result = await fileService.uploadMomentAttachment(file, mediaType, tenantId, updateProgress);
            } else {
                // 统一使用临时素材上传
                console.log('使用临时素材上传:', mediaType, fieldKey);
                result = await fileService.uploadMedia(file, mediaType, tenantId, updateProgress);
            }

            // 上传成功后处理
            if (result.success && result.mediaId) {
                console.log(`媒体上传成功 - 字段: ${fieldKey}, mediaId: ${result.mediaId}, ossUrl: ${result.ossUrl}`);

                // 保存OSS URL的字段名
                const ossUrlFieldKey = `${fieldKey}_ossUrl`;

                // 特殊处理特定字段
                if (fieldKey === 'picUrl' || fieldKey === 'thumbMediaId' || fieldKey === 'imageMediaIds') {
                    // 这些特殊字段也统一使用mediaId存储
                    console.log(`特殊字段 ${fieldKey} 使用mediaId格式存储: ${result.mediaId}`);
                }

                // 设置mediaId和ossUrl字段到表单中
                stepForm.setFieldsValue({
                    actionParams: {
                        ...stepForm.getFieldValue('actionParams'),
                        [fieldKey]: result.mediaId,
                        [ossUrlFieldKey]: result.ossUrl // 保存OSS URL
                    }
                });

                // 更新文件列表，显示已上传状态
                setFileList(prev => ({
                    ...prev,
                    [fieldKey]: [
                        {
                            uid: '-1',
                            name: file.name,
                            status: 'done',
                            url: result.previewType === 'image' ? '图片预览' : '视频预览'
                        }
                    ]
                }));

                // 添加预览URL到预览对象
                setPreviewUrls(prev => ({
                    ...prev,
                    [result.mediaId]: result.ossUrl || (result.previewType === 'image' ? '图片预览' : '视频预览')
                }));

                message.success(`${file.name} 上传成功`);

                if (onSuccess) {
                    onSuccess(result);
                }
            } else {
                // 优化错误消息，提供更具体的原因和解决方案
                let errorMsg = `${file.name} 上传失败`;
                
                // 如果有后端返回的具体错误信息
                if (result && result.message) {
                    errorMsg += `: ${result.message}`;
                } else {
                    errorMsg += ': 服务器处理失败';
                }
                
                // 添加关于企业微信限制的提示
                if (mediaType === 'image') {
                    if (isMomentAttachment) {
                        errorMsg += '\n可能原因：图片不符合企业微信朋友圈要求。请确保：\n- 图片大小不超过10MB\n- 图片格式为JPG/PNG\n- 图片长边不超过10800像素，短边不超过1080像素';
                    } else {
                        errorMsg += '\n可能原因：图片不符合企业微信要求。请确保：\n- 图片大小不超过10MB\n- 图片格式为JPG/PNG';
                    }
                } else if (mediaType === 'video') {
                    if (isMomentAttachment) {
                        errorMsg += '\n可能原因：视频不符合企业微信朋友圈要求。请确保：\n- 视频大小不超过10MB\n- 视频格式为MP4\n- 视频时长不超过30秒';
                    } else {
                        errorMsg += '\n可能原因：视频不符合企业微信要求。请确保：\n- 视频大小不超过10MB\n- 视频格式为MP4';
                    }
                }
                
                console.error('文件上传失败:', errorMsg);
                message.error(errorMsg);
                
                if (onError) {
                    onError(new Error(errorMsg));
                }
            }
        } catch (error) {
            console.error('文件上传失败:', error);
            
            // 优化错误消息，处理不同类型的错误
            let errorMsg = `${file.name} 上传失败`;
            
            // 区分网络错误和服务器错误
            if (error.message.includes('status code 500')) {
                errorMsg = `${file.name} 上传失败: 服务器处理错误`;
                
                // 添加关于企业微信限制的提示
                if (mediaType === 'image') {
                    if (isMomentAttachment) {
                        errorMsg += '\n可能原因：图片不符合企业微信朋友圈要求。请确保：\n- 图片大小不超过10MB\n- 图片格式为JPG/PNG\n- 图片长边不超过10800像素，短边不超过1080像素';
                    } else {
                        errorMsg += '\n可能原因：图片不符合企业微信要求。请确保：\n- 图片大小不超过10MB\n- 图片格式为JPG/PNG';
                    }
                } else if (mediaType === 'video') {
                    if (isMomentAttachment) {
                        errorMsg += '\n可能原因：视频不符合企业微信朋友圈要求。请确保：\n- 视频大小不超过10MB\n- 视频格式为MP4\n- 视频时长不超过30秒';
                    } else {
                        errorMsg += '\n可能原因：视频不符合企业微信要求。请确保：\n- 视频大小不超过10MB\n- 视频格式为MP4';
                    }
                }
            } else if (error.message.includes('Network Error')) {
                errorMsg += ': 网络连接错误，请检查网络后重试';
            } else {
                errorMsg += `: ${error.message}`;
            }
            
            message.error(errorMsg);

            if (onError) {
                onError(new Error(errorMsg));
            }
        } finally {
            const uploadKey = `${stepType}_${fieldKey}`;
            setUploading(prev => ({ ...prev, [uploadKey]: false, [fieldKey]: false }));
        }
    };

    // 移除已上传文件
    const handleRemoveFile = (fieldKey, stepType = 'MESSAGE') => {
        console.log(`移除文件 - 字段: ${fieldKey}, 步骤类型: ${stepType}`);

        // 创建ossUrl字段名
        const ossUrlFieldKey = `${fieldKey}_ossUrl`;

        // 清除表单字段
        stepForm.setFieldsValue({
            actionParams: {
                ...stepForm.getFieldValue('actionParams'),
                [fieldKey]: undefined,
                [ossUrlFieldKey]: undefined // 同时清除OSS URL
            }
        });

        // 清除文件列表
        setFileList(prev => ({
            ...prev,
            [fieldKey]: []
        }));

        // 清除预览URL - 从mediaId映射中清除
        const currentMediaId = stepForm.getFieldValue(['actionParams', fieldKey]);
        if (currentMediaId) {
            setPreviewUrls(prev => {
                const newUrls = { ...prev };
                delete newUrls[currentMediaId];
                return newUrls;
            });
        }
    };

    return {
        fileList,
        setFileList,
        previewUrls,
        setPreviewUrls,
        uploading,
        setUploading,
        uploadProgress,
        setUploadProgress,
        handleFileUpload,
        handleRemoveFile
    };

}